import { getDefaultStore, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { isGenericSessionArtifact } from '../artifacts/sessionArtifactFocus';
import {
  setComposerLinkedItemAtom,
  setOpenArtifactAtom,
  type Artifact,
} from '../artifacts/state';
import { pendingFirstTurnAtom } from '../pendingFirstTurn';

export interface WorkChatContextHydration {
  /** Hardcoded opening assistant turn a guided create stashed for this project. */
  openingAssistantMessage: string | undefined;
  /** Whether the hardcoded opening turn uses normal message actions. */
  openingAssistantMessageFeedback: boolean;
}

/**
 * URL-param → canvas/composer hydration for a routed Work chat.
 *
 * When the user clicks a session or patient tile we navigate here with
 * ``?artifact=…&linkName=…[&linkType=…][&linkPatient=…]`` so the chat opens
 * with the relevant artifact in the canvas and the composer pre-linked to that
 * context. This runs AFTER ``ProjectsArtifactCanvasHost``'s
 * reset-on-projectId-change clear (child effect runs before parent effect), so
 * the artifact write wins the race.
 *
 * The params are stripped off the URL after applying so a refresh doesn't
 * re-fire the hydration on top of whatever state the user has built up.
 *
 * Hydration is once per chat LIFETIME, not once per project: pass
 * `chatInstanceId` and a rotation re-arms it, so chat A → home → chat A hydrates
 * again.
 *
 * Focused-pane mode (`release-scribe-work-session-artifact-v2`): a hand-off that
 * links a WHOLE session (tile, `?artifact=session:…`) keeps the composer chip
 * but opens nothing on the canvas. The canvas host redirects generic sessions
 * to the Session page, and a chat the clinician just asked for must not bounce
 * them straight back out of it.
 */
/** The route state hydration reads. Its identity re-arms the effect on navigation. */
export interface WorkChatHydrationLocation {
  isReady: boolean;
  query: Record<string, string | string[] | undefined>;
}

export const useWorkChatContextHydrationCore = (
  {
    projectId,
    chatInstanceId,
    isWorkSessionArtifactV2Enabled,
  }: {
    projectId: string | undefined;
    chatInstanceId: string;
    isWorkSessionArtifactV2Enabled: boolean;
  },
  location: WorkChatHydrationLocation,
  replaceQuery: (query: Record<string, string | string[]>) => void
): WorkChatContextHydration => {
  const setOpenArtifactAtomValue = useSetAtom(setOpenArtifactAtom);
  const setOpenArtifact = useCallback(
    (artifact: Artifact | null) => {
      setOpenArtifactAtomValue(
        isWorkSessionArtifactV2Enabled && isGenericSessionArtifact(artifact)
          ? null
          : artifact
      );
    },
    [isWorkSessionArtifactV2Enabled, setOpenArtifactAtomValue]
  );
  const setLinkedItem = useSetAtom(setComposerLinkedItemAtom);
  const [guidedIntro, setGuidedIntro] = useState<{
    projectId: string;
    text: string;
    feedback: boolean;
  } | null>(null);
  const hydratedRef = useRef<string | null>(null);
  const hydratedForChatInstanceRef = useRef(chatInstanceId);
  if (hydratedForChatInstanceRef.current !== chatInstanceId) {
    hydratedForChatInstanceRef.current = chatInstanceId;
    hydratedRef.current = null;
  }

  useEffect(() => {
    if (!location.isReady || !projectId) return;
    if (hydratedRef.current === projectId) return;

    // Seamless hand-off: a flow that created this project elsewhere (Copilot's
    // note push, Session Work) stashed the opening turn + linked context in
    // ``pendingFirstTurnAtom`` — shared state, never the URL. Here we only
    // hydrate the canvas + composer chip from ``linked``.
    //
    // We deliberately DO NOT clear the slot: both readers are idempotent via
    // their own per-project guards (``hydratedRef`` here, ``autoSentProjectIds``
    // in the seeded-first-turn hook), and the next create overwrites the slot,
    // so leaving it set is safe.
    const pending = getDefaultStore().get(pendingFirstTurnAtom);
    if (pending && pending.projectId === projectId) {
      if (pending.openingAssistantMessage) {
        setGuidedIntro({
          projectId,
          text: pending.openingAssistantMessage,
          feedback: pending.openingAssistantMessageFeedback ?? false,
        });
      }
      const linked = pending.linked;
      setLinkedItem(linked ?? null);

      if (pending.openArtifact) {
        setOpenArtifact(pending.openArtifact);
      } else if (linked) {
        if (linked.type === 'library_document') {
          setOpenArtifact({ type: 'library-document', documentId: linked.documentId });
        } else if (linked.type === 'patient' && linked.patientId) {
          setOpenArtifact({ type: 'patient', patientId: linked.patientId });
        } else if (linked.type === 'session') {
          setOpenArtifact({ type: 'session', sessionId: linked.sessionId });
        } else {
          setOpenArtifact(null);
        }
      } else {
        setOpenArtifact(null);
      }

      hydratedRef.current = projectId;
      return;
    }

    const { artifact, linkType, linkName, linkPatient } = location.query;
    const artifactParam = typeof artifact === 'string' ? artifact : undefined;
    const name = typeof linkName === 'string' ? linkName : undefined;

    // URL hand-offs take precedence over the artifact host's per-project
    // restoration. With no hand-off params, leave the canvas alone so its last
    // artifact survives refresh/back; composer linkage remains ephemeral
    // (clearing the chip prevents bleed across sidebar project switches).
    if (artifactParam) {
      const [kind, id] = artifactParam.split(':');
      if (kind === 'patient' && id) {
        setOpenArtifact({ type: 'patient', patientId: id });
      } else if (kind === 'session' && id) {
        setOpenArtifact({ type: 'session', sessionId: id });
      } else {
        setOpenArtifact(null);
      }

      if (name) {
        if (linkType === 'patient' || kind === 'patient') {
          // A ``patient``-typed link's id (if any) lets the agent's
          // patient-scoped tools resolve a specific profile rather than
          // guessing from the display name alone.
          setLinkedItem({
            type: 'patient',
            patientName: name,
            patientId: kind === 'patient' && id ? id : undefined,
          });
        } else if (kind === 'session' && id) {
          const patientName = typeof linkPatient === 'string' ? linkPatient : undefined;
          setLinkedItem({
            type: 'session',
            sessionId: id,
            sessionName: name,
            patientName,
          });
        } else {
          setLinkedItem(null);
        }
      } else {
        setLinkedItem(null);
      }
    } else {
      setLinkedItem(null);
    }

    hydratedRef.current = projectId;

    // Strip the hydration params off the URL so a refresh doesn't re-hydrate on
    // top of mutated state. Only fire the replace when there's actually
    // something to strip — avoids a redundant history entry on the sidebar-nav
    // case.
    if (artifactParam || name || linkType || linkPatient) {
      const next = { ...location.query, id: projectId } as Record<string, unknown>;
      delete next.artifact;
      delete next.linkType;
      delete next.linkName;
      delete next.linkPatient;
      replaceQuery(next as Record<string, string | string[]>);
    }
  }, [chatInstanceId, location, projectId, replaceQuery, setOpenArtifact, setLinkedItem]);

  return {
    openingAssistantMessage:
      projectId && guidedIntro?.projectId === projectId ? guidedIntro.text : undefined,
    openingAssistantMessageFeedback:
      !!projectId && guidedIntro?.projectId === projectId && guidedIntro.feedback,
  };
};
